/**
 * WorksiteSidebar - Draggable list of active worksites.
 *
 * The cards are the drag source for the scheduling calendar. Drag lifecycle is
 * reported upward via onDragStart/onDragEnd; the dragged worksite lives in the
 * orchestrator.
 */
import type { WorksiteForPlanning } from './types';

interface WorksiteSidebarProps {
  worksites: WorksiteForPlanning[];
  onDragStart: (worksite: WorksiteForPlanning) => void;
  onDragEnd: () => void;
}

export function WorksiteSidebar({ worksites, onDragStart, onDragEnd }: WorksiteSidebarProps) {
  const worksiteColors = [
    'bg-brand',
    'bg-brand',
    'bg-purple-500',
    // r2b21[4] (ronda 4): aquí vivía `bg-signal-soft0`, una clase que el
    // design system no define — la tarjeta salía sin fondo con texto blanco
    // encima del contenedor blanco: ilegible. El sólido del token, como sus
    // hermanas de paleta (todas admiten texto blanco).
    'bg-signal',
    'bg-pink-500',
    'bg-teal-500',
  ];

  return (
    <div className="w-64 flex-shrink-0 bg-white rounded-lg border border-line overflow-hidden">
      <div className="p-4 border-b border-line bg-surface-2">
        <h2 className="font-semibold text-ink">Proyectos</h2>
        <p className="text-sm text-ink-2">Arrastra para asignar</p>
      </div>

      <div className="p-3 space-y-2 max-h-[500px] overflow-y-auto">
        {worksites.filter((p) => p.is_active).map((worksite, i) => (
          <div
            key={worksite.id}
            draggable
            onDragStart={() => onDragStart(worksite)}
            onDragEnd={onDragEnd}
            className={`
              p-3 rounded-lg cursor-grab active:cursor-grabbing
              ${worksiteColors[i % worksiteColors.length]} text-white
              shadow-sm hover:shadow-md transition-shadow
            `}
          >
            <p className="font-medium">{worksite.code}</p>
            <p className="text-sm opacity-90">{worksite.name}</p>
            <div className="flex items-center gap-2 mt-2 text-xs opacity-75">
              {/* Antes pintaba «undefined/undefined asignados»: el tipo pedía
                  campos que `GET /worksites` no trae (ronda 3, r2b21[10]).
                  Guion cuando falte el dato, nunca undefined. */}
              <span>
                {worksite.assigned_workers ?? '—'}/{worksite.required_workers ?? '—'} asignados
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
